/**
 * CFS cart state and cart mutations (build spec §5, §7; CLAUDE.md fee rules).
 *
 * - Keeps a fee-free copy of the cart (no `_mws_fee` / `service-fee` line), seeded from the
 *   inline JSON rendered by snippets/cfs-cart-state.liquid and refreshed after every
 *   `cart:lines-update` event, whoever fired it (Horizon product form, cart drawer, our cards).
 * - Wraps `CartLinesUpdateEvent.createCartFromAjaxResponse` so `cart.totalQuantity` excludes
 *   the fee for every listener (header cart bubble, screen-reader count announcement).
 * - `setVariantQuantity()` adds/changes/removes a variant with one request, asks for the cart
 *   drawer sections and the `cfs-cart-state` section, and fires Horizon's CartLinesUpdateEvent
 *   so the drawer and header re-render through their existing listeners.
 *
 * - `syncCartFromApp()` catches up after an app changes the cart itself (Bundler bundles).
 * - A few seconds after the theme's last change the cart is read again and compared with what the
 *   shopper chose (see `reconcile()`): app writes can undo a change that has already succeeded.
 *
 * Emits `cfs:cart-state` on document with the fee-free cart as `detail`.
 */
import { CartLinesUpdateEvent, CartErrorEvent, StandardEvents } from '@shopify/events';
import { fetchConfig } from '@theme/utilities';

// The apps' fetch wrappers sit around `window.fetch` and the fee app's holds every cart response
// ~0.8-1.2 s while it re-reads the cart (the apps still see the same request and response). The
// inline tap (snippets/cfs-fetch-tap.liquid) gives us the response at network time instead.
let appsSettled = Promise.resolve();

/**
 * `fetch` for the theme's own cart requests that resolves as soon as the network responds, not
 * after the app wrappers finish. Falls back to plain `fetch` when the tap isn't on the page, and
 * to the app-wrapped response when the request never passes through it (today's behaviour).
 *
 * A mutation first waits (at most 3 s) for the apps to finish reacting to the previous mutation:
 * Zapiet rewrites each line with the quantity it read earlier, which could otherwise land on top
 * of our next change and undo it.
 * @param {string} url
 * @param {RequestInit} [init] - Used as the tap key, so pass a fresh object per request.
 * @param {{ mutation?: boolean }} [options]
 * @returns {Promise<Response>}
 */
async function cartFetch(url, init = {}, { mutation = false } = {}) {
  const taps = /** @type {any} */ (window).cfsFetchTaps;
  if (!(taps instanceof WeakMap)) return fetch(url, init);

  if (mutation) {
    let timer = 0;
    await Promise.race([appsSettled, new Promise((resolve) => (timer = window.setTimeout(resolve, 3000)))]);
    clearTimeout(timer);
  }
  // Registered before the call: the innermost fetch layer looks the init object up synchronously.
  /** @type {Promise<Response>} */
  const early = new Promise((resolve, reject) => taps.set(init, { resolve, reject }));
  const wrapped = fetch(url, init);
  if (mutation) appsSettled = wrapped.then(() => {}, () => {});
  wrapped.catch(() => {});
  return Promise.race([early, wrapped]);
}

export const CART_STATE_SECTION = 'cfs-cart-state';
export const CART_STATE_EVENT = 'cfs:cart-state';

/**
 * @typedef {{ key: string, id: number, variant_id: number, product_id: number, quantity: number, final_line_price: number }} CfsLine
 * @typedef {{ token?: string, currency?: string, items: CfsLine[], item_count: number, total_price: number, total_price_formatted?: string, fee_price?: number, fee_quantity?: number, discount_codes?: any[] }} CfsCart
 */

/**
 * Whether an AJAX cart line is the service-fee line.
 * @param {any} item
 */
export function isFeeLine(item) {
  return Boolean(item?.properties?._mws_fee) || item?.handle === 'service-fee' || item?.product_handle === 'service-fee';
}

/**
 * Removes the fee line from an AJAX cart object (as returned by /cart.js or /cart/change.js).
 * @param {any} ajaxCart
 * @returns {any}
 */
export function withoutFee(ajaxCart) {
  if (!ajaxCart || !Array.isArray(ajaxCart.items)) return ajaxCart;
  const fees = ajaxCart.items.filter(isFeeLine);
  if (fees.length === 0) return ajaxCart;

  const items = ajaxCart.items.filter((item) => !isFeeLine(item));
  const feeQuantity = fees.reduce((sum, item) => sum + (item.quantity || 0), 0);
  const feePrice = fees.reduce((sum, item) => sum + (item.final_line_price || 0), 0);

  return {
    ...ajaxCart,
    items,
    item_count: Math.max(0, (ajaxCart.item_count || 0) - feeQuantity),
    total_price: Math.max(0, (ajaxCart.total_price || 0) - feePrice),
    fee_price: feePrice,
    fee_quantity: feeQuantity,
  };
}

// Make every `cart.totalQuantity` in the theme fee-free.
const originalCreateCart = CartLinesUpdateEvent.createCartFromAjaxResponse;
if (typeof originalCreateCart === 'function' && !originalCreateCart.cfsWrapped) {
  const wrapped = (/** @type {any} */ ajaxCart) => originalCreateCart.call(CartLinesUpdateEvent, withoutFee(ajaxCart));
  wrapped.cfsWrapped = true;
  CartLinesUpdateEvent.createCartFromAjaxResponse = wrapped;
}

/** @type {CfsCart} */
let state = readInlineState(document) ?? { items: [], item_count: 0, total_price: 0 };

/**
 * @param {ParentNode} root
 * @returns {CfsCart | null}
 */
function readInlineState(root) {
  const el = root.querySelector('script[data-cfs-cart-state]');
  if (!el?.textContent) return null;
  try {
    return JSON.parse(el.textContent);
  } catch (error) {
    console.warn('[cfs-cart] Could not parse cart state', error);
    return null;
  }
}

/** @param {string} html */
function stateFromSectionHtml(html) {
  return readInlineState(new DOMParser().parseFromString(html, 'text/html'));
}

/** @returns {CfsCart} */
export function getCartState() {
  return state;
}

/**
 * The cart line for a variant, if it is in the basket.
 * @param {number|string} variantId
 * @returns {CfsLine | undefined}
 */
export function lineForVariant(variantId) {
  return state.items.find((line) => String(line.variant_id) === String(variantId));
}

/** @param {CfsCart} next */
function setState(next) {
  if (!next) return;
  state = next;

  // Write only what changed: setting a node to the value it already has still queues a DOM
  // mutation record, and several apps observe the whole document and react to each one.
  const count = String(state.item_count);
  const word = state.item_count === 0 ? 'empty' : state.item_count === 1 ? 'item' : 'items';
  for (const el of document.querySelectorAll('[data-cfs-cart-count]')) {
    if (el.textContent !== count) el.textContent = count;
    if (el instanceof HTMLElement && el.dataset.count !== count) el.dataset.count = count;
  }
  for (const el of document.querySelectorAll('[data-cfs-cart-items-word]')) {
    if (el.textContent !== word) el.textContent = word;
  }
  if (state.total_price_formatted) {
    for (const el of document.querySelectorAll('[data-cfs-cart-total]')) {
      if (el.textContent !== state.total_price_formatted) el.textContent = state.total_price_formatted;
    }
  }

  document.dispatchEvent(new CustomEvent(CART_STATE_EVENT, { detail: state }));
}

async function fetchState() {
  const url = `${window.Shopify?.routes?.root ?? '/'}?sections=${CART_STATE_SECTION}`;
  const response = await fetch(url, { headers: { Accept: 'application/json' } });
  const sections = await response.json();
  return stateFromSectionHtml(sections[CART_STATE_SECTION] ?? '');
}

let refreshTimer = 0;
function scheduleRefresh() {
  clearTimeout(refreshTimer);
  refreshTimer = window.setTimeout(() => {
    fetchState()
      .then((next) => next && setState(next))
      .catch((error) => console.warn('[cfs-cart] Refresh failed', error));
  }, 50);
}

// Follow every cart change, including ones made by Horizon components and apps.
document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  /** @type {any} */ (event).promise
    ?.then((/** @type {any} */ result) => {
      const html = result?.detail?.sections?.[CART_STATE_SECTION];
      if (html) {
        const next = stateFromSectionHtml(html);
        if (next) return setState(next);
      }
      scheduleRefresh();
    })
    .catch((/** @type {any} */ error) => {
      if (error?.name !== 'AbortError') scheduleRefresh();
    });
});

// Back/forward cache restores can show a stale basket.
window.addEventListener('pageshow', (event) => {
  if (event.persisted) scheduleRefresh();
});

/** Section ids of every cart-items component on the page (drawer and cart page). */
function cartSectionIds() {
  const ids = new Set([CART_STATE_SECTION]);
  for (const el of document.querySelectorAll('cart-items-component[data-section-id]')) {
    if (el instanceof HTMLElement && el.dataset.sectionId) ids.add(el.dataset.sectionId);
  }
  return [...ids];
}

// Apps rewrite lines right after every cart change (Zapiet sets `_ZapietId` with the quantity it
// read before our change, Parcel changes line 1, the fee app removes and re-adds its line), for
// 5-10 s. Now and then one of those writes lands after ours and quietly undoes it: the basket
// shows 2 but the cart holds 1, or a MinMaxify revert is lost. So once the theme's changes have
// gone quiet, the cart is read again and compared with the quantities the theme last set. A
// difference in a variant the theme just changed is put back once (the shopper's choice); any other
// difference (an app adding or removing its own line on purpose), or one still there after the
// retry, is accepted and shown, so the basket never shows something checkout won't have.
const RECONCILE_MS = 5000;
/** @type {Map<string, number> | null} */
let expected = null;
let retried = false;
/** Variants the theme changed since the cart last matched: the only ones a retry may set. */
const touched = new Set();
let inFlight = 0;
let reconcileTimer = 0;
let reconcileReads = 0;

/** @param {{ items: { variant_id: number | string, quantity: number }[] }} cart */
function quantitiesOf(cart) {
  /** @type {Map<string, number>} */
  const map = new Map();
  for (const item of cart.items) map.set(String(item.variant_id), (map.get(String(item.variant_id)) ?? 0) + item.quantity);
  return map;
}

/**
 * Records the quantities a theme change has just produced and (re)starts the quiet-period timer.
 * @param {CfsCart} cart - The fee-free cart from the change's response.
 * @param {boolean} isRetry - A reconcile retry: keeps the one-retry budget used up.
 * @param {(number|string)[]} variantIds - The variants this change set.
 */
function expectCart(cart, isRetry, variantIds) {
  expected = quantitiesOf(cart);
  for (const id of variantIds) {
    touched.add(String(id));
    if (!expected.has(String(id))) expected.set(String(id), 0); // removed: must stay removed
  }
  if (!isRetry) retried = false;
  reconcileReads = 0;
  clearTimeout(reconcileTimer);
  reconcileTimer = window.setTimeout(reconcile, RECONCILE_MS);
}

async function reconcile() {
  const want = expected;
  if (!want) return;
  if (inFlight > 0) {
    reconcileTimer = window.setTimeout(reconcile, RECONCILE_MS);
    return;
  }
  /** @type {any} */
  let cart;
  try {
    const response = await fetch(`${window.Shopify?.routes?.root ?? '/'}cart.js`, { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error(String(response.status));
    cart = withoutFee(await response.json());
  } catch {
    // Rate limited (429) or offline: try again a little later, a couple of times at most.
    if (want === expected && ++reconcileReads < 3) reconcileTimer = window.setTimeout(reconcile, RECONCILE_MS);
    return;
  }
  if (want !== expected || inFlight > 0) return; // a newer change runs its own check

  const have = quantitiesOf(cart);
  const drifted = [...want].filter(([variantId, quantity]) => (have.get(variantId) ?? 0) !== quantity);
  if (drifted.length === 0) {
    expected = null;
    touched.clear();
    return;
  }
  const lost = drifted.filter(([variantId]) => touched.has(variantId));
  if (lost.length > 0 && !retried) {
    retried = true;
    for (const [variantId, quantity] of lost) {
      await setVariantQuantity({ variantId, quantity, isRetry: true }).catch(() => {});
    }
    // A successful retry records a new expectation and schedules the next check itself.
    if (want === expected) reconcileTimer = window.setTimeout(reconcile, RECONCILE_MS);
    return;
  }
  expected = null;
  touched.clear();
  syncCartFromApp();
}

/**
 * MinMaxify (per-category limits, e.g. max 2 pain-relief items). Its limits are per collection
 * group, not per product. It wraps `fetch` and XHR, so it does see our requests and has usually
 * judged the cart already: `checkLimits()` is true for "within limits" and is trusted at once. It is
 * also false while MinMaxify is still loading the collections of a line it hasn't seen before (a
 * product added from a card, the service-fee line): it then shows "Verifying" and fetches its
 * cart-extras (a few hundred ms). So otherwise MinMaxify is handed the cart and we wait for its next
 * settled verdict, which it reports through `window.minmaxifyDisplayCartLimits(result, changed)`
 * (a customisation hook found in its script, not a documented API), rather than re-fetching the
 * cart and sleeping. If the cart breaks a limit, the app's own message is shown (`showMessage()`)
 * and the change is reverted by the caller.
 * @param {number|string} variantId
 * @param {number} quantity - The quantity the cart should now hold.
 * @returns {Promise<boolean>} true when within limits (or MinMaxify isn't on the page, or gives no
 *   verdict within 4 s: the app still enforces its limits at checkout)
 */
async function withinMinMaxifyLimits(variantId, quantity) {
  const mm = /** @type {any} */ (window).minMaxify;
  if (!mm?.checkLimits || !mm.handleCartUpdate) return true;
  try {
    // "Seen": MinMaxify's own cart already holds the new quantity, so its verdict covers it.
    const seen = (mm.cart?.items ?? []).some(
      (/** @type {any} */ item) => String(item.variant_id ?? item.id) === String(variantId) && item.quantity >= quantity
    );
    if (seen && mm.checkLimits()) return true;

    // checkLimits() judges the cart last given to handleCartUpdate() (setting mm.cart alone leaves
    // a stale verdict). Not seen yet: hand it the fresh cart. Seen but not a "yes": re-judge its own
    // cart, which reports a settled breach again, or, while it is "Verifying", brings the verdict
    // as soon as its cart-extras load. Either way no extra /cart.js is needed.
    const cart = seen ? mm.cart : await (await cartFetch(`${window.Shopify?.routes?.root ?? '/'}cart.js`)).json();
    const verdict = nextMinMaxifyVerdict(4000);
    mm.handleCartUpdate(cart, 'update');

    let ok = Boolean(mm.checkLimits());
    if (ok) verdict.cancel();
    else ok = await verdict.promise;
    if (!ok) mm.showMessage?.();
    return ok;
  } catch {
    return true;
  }
}

/** @type {Set<(ok: boolean) => void>} */
const minMaxifyWaiters = new Set();

/**
 * The next settled MinMaxify verdict (not its "Verifying" state). MinMaxify calls
 * `window.minmaxifyDisplayCartLimits` on every state change; this installs that hook once (marked
 * `.cfs`, chaining any hook already there) and resolves the waiters from it.
 * @param {number} timeout - After this many ms resolve true: MinMaxify still enforces at checkout.
 * @returns {{ promise: Promise<boolean>, cancel: () => void }}
 */
function nextMinMaxifyVerdict(timeout) {
  const w = /** @type {any} */ (window);
  if (!w.minmaxifyDisplayCartLimits?.cfs) {
    const previous = w.minmaxifyDisplayCartLimits;
    /** @type {boolean | undefined} */
    let latest;
    let flushQueued = false;
    const hook = function (/** @type {any} */ result, /** @type {boolean} */ changedOnPage) {
      if (typeof previous === 'function') previous.apply(this, arguments);
      // One judgement reports twice in the same tick: first the verdict MinMaxify held before (stale),
      // then the new one, or "Verifying". Only the last report of a tick counts.
      latest = result && 'isOk' in result && !changedOnPage ? Boolean(result.isOk) : undefined;
      if (flushQueued) return;
      flushQueued = true;
      queueMicrotask(() => {
        flushQueued = false;
        if (latest !== undefined) for (const waiter of [...minMaxifyWaiters]) waiter(latest);
      });
    };
    hook.cfs = true;
    w.minmaxifyDisplayCartLimits = hook;
  }

  /** @type {(ok: boolean) => void} */
  let settle = () => {};
  /** @type {Promise<boolean>} */
  const promise = new Promise((resolve) => {
    const timer = window.setTimeout(() => settle(true), timeout);
    settle = (ok) => {
      clearTimeout(timer);
      minMaxifyWaiters.delete(settle);
      resolve(ok);
    };
    minMaxifyWaiters.add(settle);
  });
  return { promise, cancel: () => settle(true) };
}

/** Reads "maximum of N" from MinMaxify's popup, if it is showing. */
function minMaxifyLimitNumber() {
  const text = [...document.querySelectorAll('body *')].find((el) => el.childElementCount === 0 && /maximum of \d+/i.test(el.textContent ?? ''))?.textContent ?? '';
  return Number(/maximum of (\d+)/i.exec(text)?.[1]) || null;
}

export class CfsCartError extends Error {
  /**
   * @param {string} message
   * @param {any} [detail]
   */
  constructor(message, detail) {
    super(message);
    this.name = 'CfsCartError';
    this.detail = detail;
  }
}

/**
 * Sets a variant's basket quantity: adds it when absent, changes the line when present,
 * removes it at 0. One request; the drawer and header update via CartLinesUpdateEvent.
 *
 * @param {object} options
 * @param {number|string} options.variantId
 * @param {number} options.quantity - The new absolute quantity.
 * @param {EventTarget} [options.source] - Element that dispatches the cart event.
 * @param {boolean} [options.isRetry] - Internal: a reconcile or vanished-line retry.
 * @returns {Promise<CfsCart>} The fee-free cart after the change.
 */
export async function setVariantQuantity({ variantId, quantity, source = document, isRetry = false }) {
  inFlight += 1;
  try {
    return await changeVariantQuantity({ variantId, quantity, source, isRetry });
  } finally {
    inFlight -= 1;
  }
}

/**
 * Turns a cart API error description into words for shoppers (the raw text is technical).
 * @param {string} raw
 */
function friendlyCartError(raw) {
  const stock = /only (\d+)/i.exec(raw);
  if (stock) return `Only ${stock[1]} available`;
  if (/sold out/i.test(raw)) return 'Sorry, this is sold out';
  return 'Could not update your basket';
}

/**
 * @param {{ variantId: number|string, quantity: number, source: EventTarget, isRetry: boolean, canFallback?: boolean }} options
 * @returns {Promise<CfsCart>}
 */
async function changeVariantQuantity({ variantId, quantity, source, isRetry, canFallback = true }) {
  const line = lineForVariant(variantId);
  if (!line && quantity <= 0) return state; // nothing to remove
  const sections = cartSectionIds();
  const sectionsUrl = window.location.pathname;
  const deferred = CartLinesUpdateEvent.createPromise();

  /** @type {'add' | 'update' | 'remove'} */
  let action = 'update';
  /** @type {string} */
  let url;
  /** @type {object} */
  let body;

  if (!line) {
    action = 'add';
    url = Theme.routes.cart_add_url;
    body = { items: [{ id: Number(variantId), quantity }], sections, sections_url: sectionsUrl };
  } else {
    action = quantity > 0 ? 'update' : 'remove';
    url = `${Theme.routes.cart_change_url}.js`;
    // Change by variant id, not line key: Zapiet rewrites every line's properties
    // (`_ZapietId`) after cart changes, which changes the line key without a cart event.
    body = { id: String(variantId), quantity, sections, sections_url: sectionsUrl };
  }

  source.dispatchEvent(
    new CartLinesUpdateEvent({
      action,
      context: 'product',
      lines: [line ? { id: line.key, quantity } : { merchandiseId: String(variantId), quantity }],
      promise: deferred.promise,
    })
  );

  try {
    const response = await cartFetch(url, fetchConfig('json', { body: JSON.stringify(body) }), { mutation: true });
    // A 429 or gateway error can come back with an empty or HTML body.
    const data = await response.json().catch(() => ({ status: response.status || 500, description: '' }));

    if (!response.ok || data.status) {
      const raw = String(data.description || data.message || '');
      const current = await fetchState().catch(() => state);
      if (current) setState(current);
      // The line had already gone (an app's write removed it): add the variant instead, once.
      if (line && canFallback && /no valid id or line/i.test(raw) && !lineForVariant(variantId)) {
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(current ?? state),
          detail: { items: (current ?? state).items, source: 'cfs-product-card', didError: false },
        });
        return changeVariantQuantity({ variantId, quantity, source, isRetry, canFallback: false });
      }
      const message = friendlyCartError(raw);
      source.dispatchEvent(new CartErrorEvent({ error: message, code: 'INVALID', detail: data }));
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(current ?? state),
        detail: { didError: true, items: (current ?? state).items, source: 'cfs-product-card' },
      });
      throw new CfsCartError(message, data);
    }

    const next = stateFromSectionHtml(data.sections?.[CART_STATE_SECTION] ?? '') ?? (await fetchState());
    if (!next) throw new CfsCartError('Could not read basket');

    // Category limits (MinMaxify): revert an increase that breaks a limit.
    const before = line?.quantity ?? 0;
    if (quantity > before && !(await withinMinMaxifyLimits(variantId, quantity))) {
      const limit = minMaxifyLimitNumber();
      setState(next);
      // The cart should end at the old quantity: if the revert below fails or is overwritten by an
      // app, the reconcile check sends it again.
      expectCart({ ...next, items: next.items.map((item) => (String(item.variant_id) === String(variantId) ? { ...item, quantity: before } : item)) }, isRetry, [variantId]);
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(next),
        detail: { sections: data.sections ?? undefined, items: next.items, itemCount: next.item_count, source: 'cfs-product-card', didError: false },
      });
      await changeVariantQuantity({ variantId, quantity: before, source, isRetry: true, canFallback: false }).catch(() => {});
      throw new CfsCartError(limit ? `Max ${limit} per order from this category` : 'Limit reached for this category', { limit: limit ?? true });
    }

    setState(next);
    expectCart(next, isRetry, [variantId]);
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(next),
      detail: {
        sections: data.sections ?? undefined,
        items: next.items,
        itemCount: next.item_count,
        source: 'cfs-product-card',
        didError: false,
      },
    });

    return next;
  } catch (error) {
    if (error instanceof CfsCartError) throw error;
    deferred.reject(error);
    source.dispatchEvent(new CartErrorEvent({ error: 'Could not update your basket', code: 'SERVICE_UNAVAILABLE' }));
    throw new CfsCartError('Could not update your basket', { cause: error });
  }
}

/**
 * Adds several variants in one request (e.g. "Add last order to basket", T6.2).
 * The drawer and header update via CartLinesUpdateEvent, as for single changes.
 *
 * @param {object} options
 * @param {{ id: number|string, quantity: number }[]} options.items
 * @param {EventTarget} [options.source] - Element that dispatches the cart event.
 * @returns {Promise<CfsCart>} The fee-free cart after the change.
 */
export async function addVariants({ items, source = document }) {
  inFlight += 1;
  try {
    return await addVariantsNow({ items, source });
  } finally {
    inFlight -= 1;
  }
}

/**
 * @param {{ items: { id: number|string, quantity: number }[], source: EventTarget }} options
 * @returns {Promise<CfsCart>}
 */
async function addVariantsNow({ items, source }) {
  const sections = cartSectionIds();
  const deferred = CartLinesUpdateEvent.createPromise();
  source.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: items.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity })),
      promise: deferred.promise,
    })
  );

  try {
    const body = {
      items: items.map((item) => ({ id: Number(item.id), quantity: item.quantity })),
      sections,
      sections_url: window.location.pathname,
    };
    const response = await cartFetch(Theme.routes.cart_add_url, fetchConfig('json', { body: JSON.stringify(body) }), { mutation: true });
    const data = await response.json().catch(() => ({ status: response.status || 500, description: '' }));
    const next =
      (response.ok && !data.status && stateFromSectionHtml(data.sections?.[CART_STATE_SECTION] ?? '')) ||
      (await fetchState().catch(() => state));
    if (next) setState(next);
    if (next && response.ok && !data.status) expectCart(next, false, items.map((item) => item.id));
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(next ?? state),
      detail: {
        sections: response.ok ? data.sections ?? undefined : undefined,
        items: (next ?? state).items,
        itemCount: (next ?? state).item_count,
        source: 'cfs-product-card',
        didError: !response.ok || Boolean(data.status),
      },
    });
    if (!response.ok || data.status) {
      const message = friendlyCartError(String(data.description || data.message || ''));
      throw new CfsCartError(message === 'Could not update your basket' ? 'Could not add to your basket' : message, data);
    }
    return next ?? state;
  } catch (error) {
    if (error instanceof CfsCartError) throw error;
    deferred.reject(error);
    source.dispatchEvent(new CartErrorEvent({ error: 'Could not add to your basket', code: 'SERVICE_UNAVAILABLE' }));
    throw new CfsCartError('Could not add to your basket', { cause: error });
  }
}

/**
 * Re-reads the cart after an app changed it behind the theme's back, e.g. Bundler's own
 * "Add to cart" (it posts to /cart/add.js itself): fresh fee-free state, drawer and cart page
 * re-rendered through the usual cart event, and optionally the drawer opened.
 * @param {{ openDrawer?: boolean }} [options]
 */
export async function syncCartFromApp({ openDrawer = false } = {}) {
  const deferred = CartLinesUpdateEvent.createPromise();
  // "update" unless asked to open: an "add" event auto-opens the drawer when that setting is on.
  document.dispatchEvent(
    new CartLinesUpdateEvent({ action: openDrawer ? 'add' : 'update', context: 'product', lines: [], promise: deferred.promise })
  );
  try {
    const root = window.Shopify?.routes?.root ?? '/';
    const sections = await (await fetch(`${root}?sections=${cartSectionIds().join(',')}`)).json();
    const next = stateFromSectionHtml(sections[CART_STATE_SECTION] ?? '') ?? state;
    setState(next);
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(next),
      detail: { sections, items: next.items, itemCount: next.item_count, source: 'cfs-app', didError: false },
    });
  } catch (error) {
    deferred.reject(error);
    return;
  }
  if (openDrawer) /** @type {any} */ (document.querySelector('cart-drawer-component')?.closest('theme-drawer'))?.open?.();
}

// Bundler adds a whole bundle with its own request; show it in the basket straight away, then
// once more after the fee and delivery apps have finished adjusting the new lines.
document.addEventListener('bndlr:bundle_added_to_cart', () => {
  expected = null; // the bundle's lines are the app's change, not a lost update
  touched.clear();
  syncCartFromApp({ openDrawer: true });
  window.setTimeout(() => syncCartFromApp(), 3000);
});
